import dayjs from 'dayjs';
import type { AxiosInstancePlus } from '@tencent/oneid-request';
import { JSONParse, JSONStringify } from '@tencent/oneid-utils';
import { APP_TYPE, LOCAL_STORAGE, SWITCH_OPTION } from 'common/constants/Constants';
import { IResponseType } from 'common/constants/Types';
import { http } from 'common/http';

export interface IAccountDefinition {
  /** 类型， 1 企业注销 2个人注销 3 首页菜单 4 组织架构数据编辑5 可见范围菜单6 办公套件菜单7 管理员角色菜单8 agent管理菜单9 自建应用菜单 10 sso配置 */
  definition_type?: number;
  /** 是否需要配置列表详情 */
  need_detail?: boolean;
}

export interface IAppsConfig {
  app_types?: string;
  need_off_tips?: string;
}

/** 应用详情 */
export type TDefinitionInfo = {
  /** 应用id */
  app_id: string;
  /** 应用名称 */
  app_name: string;
  /** 应用类型 */
  app_type: string;
  /** 类型 */
  definition_type: number;
  /** 描述 */
  description: string;
  /** 开关状态 */
  switch_option: boolean;
  /** 应用图标 */
  logo_url: string;
};

export interface IDefinitionItem {
  /** 类型 */
  definition_type: number;
  /** 开关状态 */
  switch_option: SWITCH_OPTION;
  /** 配置列表详情 */
  definition_items?: TDefinitionInfo[];
}

export interface IAccountDefinitionResponse {
  /** 企业自定义配置 */
  account_definitions: IDefinitionItem[];
}

export interface IAppConfig {
  /** 应用类型 */
  app_type: APP_TYPE;
  /** 应用名称 */
  app_name: string;
  /** 个人退出提示 */
  personal_off_tips: IOffTips;
  /** 账号解散提示 */
  account_off_tips: IOffTips;
  /** 个人拒绝退出提示 */
  personal_reject_off_tips: IOffTips;
  /** 账号拒绝解散提示 */
  account_reject_off_tips: IOffTips;
  /** logo URI */
  logo_uri: string;
  /** 带文字的logo URI */
  logo_with_text_uri: string;
  /** 背景图 URI */
  background_uri: string;
  /** 政策 URI */
  policy_uri: string;
  /** 服务条款 URI */
  tos_uri: string;
  /** 协议模板 */
  agreement_template: string;
}

export interface IOffTips {
  /** 头部提示 */
  header_tip: string;
  /** 提示项列表 */
  tip_items: ITipItem[];
}

export interface ITipItem {
  /** 项目标题 */
  title: string;
  /** 子项目列表 */
  sub_list?: ISubListItem[];
}

export interface ISubListItem {
  /** 标题 */
  sub_title: string;
  /** 内容 */
  content: string;
  /** 图标 */
  icon: string;
}

export interface IAppsConfigResponse {
  [key: string]: IAppConfig;
}

/** 解散/退出企业的请求参数 */
export interface IAccountDismiss {
  /** 用户填写的验证码 */
  verification_code: string;
  /** 验证码接口返回的session */
  verification_session_id: string;
}

/** 个人退出的请求参数 */
export interface IUsersLogoff extends IAccountDismiss {
  /** 验证码的类型（0 手机 1 邮箱 默认手机） */
  type?: number;
}

export interface ICheckGray {
  /** 获取指定场景的灰度配置 */
  features?: string[];
  /** 是否获取所有灰度配置 */
  is_all?: boolean;
}

/** cos cookie存储的信息 */
type TCosCookieInfo = { expireTime: string; unionId: string };

class AdminApi {
  axiosInstance: AxiosInstancePlus = http;

  /**
   * 查询企业配置，获取页面、按钮开关控制数据
   */
  getAccountDefinition(
    params?: IAccountDefinition,
  ): Promise<IResponseType<IAccountDefinitionResponse>> {
    return http.get('/admin/v2/account/definition', {
      params,
    });
  }

  /**
   * 查询应用配置信息
   */
  getAppsConfig(params?: IAppsConfig): Promise<IResponseType<IAppsConfigResponse>> {
    return this.axiosInstance.get('/admin/bff/config/apps', {
      params,
    });
  }

  /**
   * 解散企业
   */
  accountDismiss(data: IAccountDismiss): Promise<{ errCode?: string }> {
    return this.axiosInstance.delete('/admin/v2/account/dismiss', {
      data,
    });
  }

  /**
   * 退出企业
   */
  usersLogoffBySelf(data: IUsersLogoff): Promise<{ errCode?: string }> {
    return this.axiosInstance.delete('/admin/v2/users/logoff_by_self', {
      data,
    });
  }

  /**
   * 图片送审
   */
  imageSafeCheck(params: { img_url: string }): Promise<IResponseType<{ img_url: string }>> {
    return this.axiosInstance.post('/admin/v2/account/image_safe_check', params);
  }

  /**
   * 调用接口，往浏览器中种入访问cos桶需要的cookie
   */
  async setCosCookie(unionId: string): Promise<IResponseType | undefined> {
    const cosCookieInfoStr = localStorage.getItem(LOCAL_STORAGE.COS_COOKIE_INFO);
    let cosCookieInfo: TCosCookieInfo | undefined = undefined;
    if (cosCookieInfoStr) {
      cosCookieInfo = JSONParse(cosCookieInfoStr);
    }
    if (
      cosCookieInfo &&
      cosCookieInfo.unionId === unionId &&
      new Date(cosCookieInfo.expireTime) > new Date()
    )
      return;
    try {
      const setCosCookieRes: IResponseType = await this.axiosInstance.get(
        '/admin/v2/account/cos/set_cookie',
      );
      localStorage.setItem(
        LOCAL_STORAGE.COS_COOKIE_INFO,
        JSONStringify({
          expireTime: dayjs().add(setCosCookieRes.expire_seconds, 's').toDate().toString(),
          unionId,
        }),
      );
      return setCosCookieRes;
    } catch {
      return;
    }
  }

  /**
   * 通用灰度接口
   * @returns { result: Record<string, boolean> }
   */
  checkGray(data: ICheckGray): Promise<IResponseType<{ result: Record<string, boolean> }>> {
    return this.axiosInstance.post('/admin/v2/check_gray', data);
  }
}

export default new AdminApi();
